import { CheckCircle } from 'lucide-react';
import { ACTION_ICON_COLOR } from '../../styles';
import { NodeCategory, OutputType, type LBNodeInfo } from '../../types';
import { defaultNodeData } from '../../graphUtils';
import { ResolveAlertActionCustomization } from './ResolveAlertActionCustomization';
import {
  collectErrors,
  validateTemplateChunks,
  validateVariableRef,
  type ValidationError,
} from '../../validation';

export const ResolveAlertActionFields = {
  SHIPMENT: 'Shipment',
  ALERT_STATUS: 'Alert status',
  COMMENT: 'Comment',
} as const;

export const ResolveAlertActionNodeInfo: LBNodeInfo = {
  protobufNodeDataKey: 'resolve_alert_action',

  category: NodeCategory.ACTION,

  label: 'Update alert status',

  hasInput: true,

  requiresShipmentInContext: true,

  getIcon: (size: number) => (
    <CheckCircle size={size} color={ACTION_ICON_COLOR} />
  ),

  getSubtitle: (nodeData) => {
    const status = nodeData.resolve_alert_action?.alert_status;
    const alertTypes = nodeData.resolve_alert_action?.alert_types || [];
    const selectAll = nodeData.resolve_alert_action?.select_all_alert_types;

    if (!status) return undefined;

    // Build subtitle with status
    let subtitle = `Alert ${status.charAt(0).toUpperCase() + status.slice(1)}`;

    // Add alert types info
    if (selectAll) {
      subtitle += ' (All types)';
    } else if (alertTypes.length > 0) {
      subtitle += ` (${alertTypes.length} type${alertTypes.length > 1 ? 's' : ''})`;
    }

    return subtitle;
  },

  getValidationErrors: (nodeId, nodeData, graph) => {
    const d = nodeData.resolve_alert_action;

    const shipmentError = validateVariableRef(
      d?.shipment_id,
      nodeId,
      graph,
      ResolveAlertActionFields.SHIPMENT
    );

    const commentErrors = validateTemplateChunks(
      d?.comment_chunks ?? [],
      nodeId,
      graph,
      ResolveAlertActionFields.COMMENT
    );

    let alertStatusError: ValidationError | undefined;
    if (!d?.alert_status) {
      alertStatusError = {
        type: 'REQUIRED_FIELD',
        field: ResolveAlertActionFields.ALERT_STATUS,
        message: 'Alert status is required',
      };
    }

    return collectErrors(shipmentError, ...commentErrors, alertStatusError);
  },

  outputType: OutputType.SINGLE,

  CustomizationComponent: ResolveAlertActionCustomization,
  createDefaultNodeData: () =>
    defaultNodeData('resolve_alert_action', {
      shipment_id: undefined,
      alert_status: 'resolved',
      comment_chunks: [],
      alert_types: [],
      select_all_alert_types: false,
    }),
};
